import { motion } from "motion/react"
import {
  AlertCircle,
  Check,
  Send01,
  Trash01,
  UserPlus01,
} from "@untitledui/icons"
import { DesignSelect } from "@/components/ui/design-select"
import { US_STATES } from "@/components/ui/us-state-select"
import { cx } from "@/lib/utils/cx"

// Shared row-card primitive for the group flow's two onboarding steps:
//
//   Step 4 (Dealerships) — every field on a row is required AND each row
//     ends with an explicit "Invite GM & add dealership" button. The user
//     fills the row and *clicks* to commit; submit on this step is per-row,
//     not per-form. The Continue at the bottom of the step is gated on at
//     least one row reaching the `sent` state.
//
//   Step 5 (Body shops) — the row is fully optional. Name + city + state
//     are required if the user adds one, but the GM invite section is
//     opt-in (you're allowed to add a shop without inviting anyone). The
//     button label flips to "Send invite & add body shop" when the invite
//     section is open, "Add body shop" otherwise. Continue can skip the
//     whole step.
//
// The card holds three visual states: `draft` (form), `sending` (button
// spinner), `sent` (condensed read-only summary with a remove). Validation
// happens in the parent step before calling `onSend`; we just surface the
// errors back via `row.errors`.

// Defaults for the row's `gmRole` field. The dropdown was removed (per
// designer: GM-only on the onboarding row, pick other roles later from
// Settings → Members), but the role still ships on the BE invite payload
// for body-shops, so we keep the constants as the default value source.
export const DEALERSHIP_ROLES = ["General Manager"] as const
export const BODYSHOP_ROLES = ["General Manager"] as const

export type TeamRowStatus = "draft" | "sending" | "sent" | "error"

export interface TeamRow {
  key: number
  name: string
  city: string
  state: string
  // GM invite fields. Only the email is exposed in the form — the role for
  // the first invite is always General Manager (per designer: pick other
  // roles later from Settings → Members & invites once the team exists).
  // The role is still tracked here so the sent-state summary can render
  // "Invited <email> as General Manager".
  gmEmail: string
  gmRole: string
  // Lifecycle.
  status: TeamRowStatus
  // Set when the row finishes a successful Send. Used by the parent step
  // to thread the team id forward (e.g., body shops use the dealership
  // team ids in the "Routes to" chip group).
  createdTeamId?: number
  errors?: {
    name?: string
    city?: string
    state?: string
    gmEmail?: string
  }
}

export const isLooseEmail = (v: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim())

// Three-mode validation. The parent step picks the mode:
//   "required" — dealership default. Email + role are mandatory.
//   "optional" — body-shop default. Email + role are optional, but if the
//                user typed an email it must be valid and a role picked.
//   "hidden"   — the user toggled the step-level "Invite GMs later"
//                toolbar. Invite fields aren't even rendered, no invite
//                will be sent, no validation runs on them.
export type InviteMode = "required" | "optional" | "hidden"

export function validateRowFields(
  r: TeamRow,
  inviteMode: InviteMode,
): { row: TeamRow; ok: boolean; willInvite: boolean } {
  const errors: TeamRow["errors"] = {}
  let ok = true
  if (!r.name.trim()) {
    errors.name = "Name is required"
    ok = false
  }
  if (!r.city.trim()) {
    errors.city = "City is required"
    ok = false
  }
  if (!r.state.trim()) {
    errors.state = "Pick a state"
    ok = false
  }

  const emailFilled = r.gmEmail.trim().length > 0
  let willInvite = false
  if (inviteMode === "required") {
    if (!emailFilled) {
      errors.gmEmail = "Email is required to send the invite"
      ok = false
    } else if (!isLooseEmail(r.gmEmail)) {
      errors.gmEmail = "Enter a valid email"
      ok = false
    }
    willInvite = ok
  } else if (inviteMode === "optional" && emailFilled) {
    if (!isLooseEmail(r.gmEmail)) {
      errors.gmEmail = "Enter a valid email"
      ok = false
    }
    willInvite = ok
  }

  return {
    row: { ...r, errors: Object.keys(errors).length ? errors : undefined },
    ok,
    willInvite,
  }
}

interface TeamRowCardProps {
  row: TeamRow
  index: number
  isPrimary: boolean
  canRemove: boolean
  palette: "indigo" | "amber"
  placeholderName: string
  // Drives the GM-invite block: shown REQUIRED, shown OPTIONAL, or hidden
  // entirely (when the parent step's "Invite GMs later" toolbar is on).
  inviteMode: InviteMode
  // Send-button label for the row's primary action. The parent supplies
  // the wording so the dealership step can read "Invite GM & add
  // dealership" while the body-shop step reads "Add body shop" or "Send
  // invite & add body shop" depending on the invite toggle.
  sendLabel: string
  // Used only on the body-shops step's `sent` summary line. Renders the
  // "Routes to: A, B" suffix beneath the success state.
  sentSummary?: string
  onChange: (patch: Partial<TeamRow>) => void
  onSend: () => void
  onRemove: () => void
  // Optional slot rendered after the standard fields; used by the
  // body-shops step to drop in the "Routes to" chip group inside the same
  // card without having to duplicate the surrounding chrome.
  extra?: React.ReactNode
}

export function TeamRowCard({
  row,
  index,
  isPrimary,
  canRemove,
  palette,
  placeholderName,
  inviteMode,
  sendLabel,
  sentSummary,
  onChange,
  onSend,
  onRemove,
  extra,
}: TeamRowCardProps) {
  const requireInvite = inviteMode === "required"
  const showInvite = inviteMode !== "hidden"
  const isSent = row.status === "sent"
  const isSending = row.status === "sending"
  const focusRing =
    palette === "indigo"
      ? "focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10"
      : "focus:border-amber-500 focus:ring-4 focus:ring-amber-500/10"
  const hasError = !!row.errors && Object.keys(row.errors).length > 0

  // ── Sent state — collapsed read-only summary ────────────────────────
  if (isSent) {
    return (
      <motion.div
        layout
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.97 }}
        transition={{ type: "spring", stiffness: 320, damping: 28 }}
        className={cx(
          "rounded-2xl border p-3.5 flex items-start gap-3",
          palette === "indigo"
            ? "border-indigo-200 bg-indigo-50/40"
            : "border-amber-200 bg-amber-50/40",
        )}
      >
        <div
          className={cx(
            "size-8 rounded-full grid place-items-center shrink-0 shadow-sm",
            palette === "indigo"
              ? "bg-indigo-500 shadow-indigo-600/30"
              : "bg-amber-500 shadow-amber-600/30",
          )}
        >
          <Check className="size-4 text-white" strokeWidth={3} aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p
              className={cx(
                "text-[14px] font-semibold truncate",
                palette === "indigo" ? "text-indigo-900" : "text-amber-900",
              )}
            >
              {row.name.trim()}
            </p>
            <span className="text-[11px] text-zinc-400 truncate">
              {[row.city, row.state].filter(Boolean).join(", ")}
            </span>
          </div>
          {row.gmEmail.trim() && (
            <p className="text-[11.5px] text-zinc-500 mt-0.5 truncate">
              <UserPlus01 className="size-3 inline mr-1 -mt-0.5" aria-hidden />
              Invited <span className="font-semibold">{row.gmEmail}</span> as{" "}
              <span className="font-semibold">{row.gmRole}</span>
            </p>
          )}
          {sentSummary && (
            <p className="text-[11.5px] text-zinc-500 mt-0.5 truncate">{sentSummary}</p>
          )}
        </div>
        <button
          type="button"
          onClick={onRemove}
          className="shrink-0 size-8 rounded-lg text-zinc-400 hover:text-red-600 hover:bg-red-50 grid place-items-center transition-all duration-150"
          aria-label="Remove"
        >
          <Trash01 className="size-3.5" aria-hidden />
        </button>
      </motion.div>
    )
  }

  // ── Draft state — full form with per-row Send action ────────────────
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6, scale: 0.97 }}
      transition={{ type: "spring", stiffness: 320, damping: 28 }}
      className={cx(
        "rounded-2xl border bg-white p-3 transition-colors duration-200",
        hasError ? "border-red-300" : "border-zinc-200",
      )}
    >
      {isPrimary && (
        <div className="flex items-center gap-1.5 mb-2">
          <span
            className={cx(
              "text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded",
              palette === "indigo"
                ? "bg-indigo-50 text-indigo-700"
                : "bg-amber-50 text-amber-700",
            )}
          >
            Primary
          </span>
          <span className="text-[11px] text-zinc-500">Where you operate from</span>
        </div>
      )}

      {/* Labels above every field — matches the pattern earlier onboarding
          steps already use (Field component). Keeping the look consistent
          across all five steps so the user never wonders where the field
          name lives. */}
      <div className="grid grid-cols-[1fr_auto] gap-2 items-start">
        <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] gap-3">
          <div className="space-y-1">
            <FieldLabel required>{placeholderLabel(palette)} name</FieldLabel>
            <input
              value={row.name}
              onChange={(e) => onChange({ name: e.target.value })}
              placeholder={placeholderName}
              maxLength={255}
              className={cx(
                "h-11 w-full rounded-xl border bg-white px-3.5 text-[14px] text-zinc-900 placeholder:text-zinc-400 outline-none transition-all duration-150",
                row.errors?.name ? "border-red-300" : "border-zinc-200",
                focusRing,
              )}
            />
          </div>
          <div className="space-y-1">
            <FieldLabel required>City</FieldLabel>
            <input
              value={row.city}
              onChange={(e) => onChange({ city: e.target.value })}
              placeholder="e.g. Austin"
              maxLength={120}
              className={cx(
                "h-11 w-full rounded-xl border bg-white px-3.5 text-[14px] text-zinc-900 placeholder:text-zinc-400 outline-none transition-all duration-150",
                row.errors?.city ? "border-red-300" : "border-zinc-200",
                focusRing,
              )}
            />
          </div>
        </div>
        <button
          type="button"
          onClick={onRemove}
          disabled={!canRemove || isSending}
          className="shrink-0 size-11 mt-6 rounded-lg text-zinc-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-zinc-400 grid place-items-center transition-all duration-150"
          aria-label={`Remove row ${index + 1}`}
        >
          <Trash01 className="size-4" aria-hidden />
        </button>
      </div>

      <div className="mt-3 space-y-1">
        <FieldLabel required>State</FieldLabel>
        <DesignSelect.Root
          value={row.state}
          onValueChange={(v) => onChange({ state: v })}
        >
          <DesignSelect.Trigger
            className={cx(
              "w-full h-11 rounded-xl border bg-white px-3.5 text-[14px] font-medium",
              row.state ? "text-zinc-900" : "text-zinc-400",
              row.errors?.state ? "border-red-300" : "border-zinc-200",
              "hover:border-zinc-300 hover:bg-white",
              "data-[state=open]:bg-white data-[state=open]:border-zinc-300",
            )}
          >
            <DesignSelect.Value placeholder="Pick a state" />
          </DesignSelect.Trigger>
          <DesignSelect.Content>
            {US_STATES.map((s) => (
              <DesignSelect.Item key={s} value={s}>
                {s}
              </DesignSelect.Item>
            ))}
          </DesignSelect.Content>
        </DesignSelect.Root>
      </div>

      {/* GM email — only field. The first invite is always to the GM, who's
          the team's day-to-day contact. Other roles (Service Manager /
          Estimator / etc.) get invited later from Settings → Members &
          invites once the team exists. */}
      {showInvite && (
        <div className="mt-3 space-y-1">
          <FieldLabel
            optional={!requireInvite}
            required={requireInvite}
          >
            GM email
          </FieldLabel>
          <input
            type="email"
            value={row.gmEmail}
            onChange={(e) => onChange({ gmEmail: e.target.value })}
            placeholder={
              requireInvite ? "manager@company.com" : "Leave blank to skip invite"
            }
            className={cx(
              "h-11 w-full rounded-xl border bg-white px-3.5 text-[14px] text-zinc-900 placeholder:text-zinc-400 outline-none transition-all duration-150",
              row.errors?.gmEmail ? "border-red-300" : "border-zinc-200",
              focusRing,
            )}
          />
          <p className="text-[11.5px] text-zinc-400">
            They'll get an invite as General Manager.
          </p>
        </div>
      )}

      {extra}

      {hasError && (
        <p className="text-[11.5px] text-red-600 mt-2 flex items-start gap-1.5">
          <AlertCircle className="size-3.5 mt-0.5 shrink-0" aria-hidden />
          <span>
            {row.errors?.name ??
              row.errors?.city ??
              row.errors?.state ??
              row.errors?.gmEmail}
          </span>
        </p>
      )}

      {/* Per-row primary action. Wording supplied by the parent step. The
          actual addDealership / createBodyshop / connect / invite calls
          live in the parent's `onSend` so each step can sequence its API
          chain (and surface its own toasts) without coupling the card to
          backend specifics. */}
      <button
        type="button"
        onClick={onSend}
        disabled={isSending}
        className={cx(
          "mt-3 w-full h-11 rounded-xl text-[14px] font-semibold flex items-center justify-center gap-2 transition-all duration-150 disabled:opacity-60",
          palette === "indigo"
            ? "bg-indigo-600 hover:bg-indigo-700 text-white shadow-md shadow-indigo-600/25"
            : "bg-amber-600 hover:bg-amber-700 text-white shadow-md shadow-amber-600/25",
        )}
      >
        {isSending ? (
          <span className="size-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
        ) : (
          <>
            <Send01 className="size-4" aria-hidden />
            {sendLabel}
          </>
        )}
      </button>
    </motion.div>
  )
}

function FieldLabel({
  children,
  required,
  optional,
}: {
  children: React.ReactNode
  required?: boolean
  optional?: boolean
}) {
  return (
    <label className="flex items-center gap-1 text-[12px] font-semibold text-zinc-700">
      {children}
      {required && (
        <span className="text-red-500" aria-hidden>
          *
        </span>
      )}
      {optional && !required && (
        <span className="text-zinc-400 text-[10px] font-bold tracking-wide">
          OPTIONAL
        </span>
      )}
    </label>
  )
}

/* ── GM-invite step-level toolbar ─────────────────────────
 *
 * Onboarding steps that have per-row GM invites render this above their
 * row list. It's a single, visible affordance for "I don't have GM
 * details right now — let me add the teams without inviting and I'll
 * send invites later from Settings." Toggling it ON hides every row's
 * invite block (TeamRowCard receives inviteMode="hidden") and the per-row
 * Send button works without email/role.
 */
export function InviteLaterToolbar({
  on,
  onChange,
  unitLabel,
}: {
  on: boolean
  onChange: (on: boolean) => void
  // "dealership" / "body shop" — used in the toolbar copy.
  unitLabel: string
}) {
  if (on) {
    return (
      <div className="flex items-center gap-2 px-3.5 py-2.5 rounded-xl bg-amber-50/70 border border-amber-200/80">
        <AlertCircle className="size-4 text-amber-600 shrink-0" aria-hidden />
        <p className="text-[12.5px] text-amber-900/85 flex-1">
          GM invites are off — you'll add the {unitLabel}s without inviting
          anyone. Send invites later from Settings → Team.
        </p>
        <button
          type="button"
          onClick={() => onChange(false)}
          className="text-[12px] font-semibold text-amber-900 underline underline-offset-2 hover:text-amber-700 whitespace-nowrap shrink-0"
        >
          Turn back on
        </button>
      </div>
    )
  }
  return (
    <div className="flex items-center gap-2 px-3.5 py-2.5 rounded-xl bg-zinc-50 border border-zinc-200">
      <UserPlus01 className="size-4 text-zinc-500 shrink-0" aria-hidden />
      <p className="text-[12.5px] text-zinc-600 flex-1">
        Inviting the GM as you add each {unitLabel}.
      </p>
      <button
        type="button"
        onClick={() => onChange(true)}
        className="text-[12px] font-semibold text-zinc-700 hover:text-zinc-900 whitespace-nowrap shrink-0"
      >
        Skip — I'll invite GMs later
      </button>
    </div>
  )
}

// Field naming differs by row type — "Dealership name" vs "Body shop name".
function placeholderLabel(palette: "indigo" | "amber") {
  return palette === "indigo" ? "Dealership" : "Body shop"
}

export function AddRowButton({
  onClick,
  label,
  palette = "emerald",
}: {
  onClick: () => void
  label: string
  palette?: "emerald" | "amber"
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cx(
        "w-full h-11 rounded-xl border border-dashed border-zinc-300 text-[13px] font-semibold text-zinc-600 transition-all duration-150 flex items-center justify-center gap-1.5",
        palette === "emerald"
          ? "hover:border-emerald-400 hover:text-emerald-700 hover:bg-emerald-50/40"
          : "hover:border-amber-400 hover:text-amber-700 hover:bg-amber-50/40",
      )}
    >
      {label}
    </button>
  )
}

let dealershipRowKey = 1
let bodyshopRowKey = 1
export const makeDealershipRow = (): TeamRow => ({
  key: dealershipRowKey++,
  name: "",
  city: "",
  state: "",
  gmEmail: "",
  gmRole: DEALERSHIP_ROLES[0],
  status: "draft",
})
export const makeBodyshopRow = (): TeamRow => ({
  key: bodyshopRowKey++,
  name: "",
  city: "",
  state: "",
  gmEmail: "",
  gmRole: BODYSHOP_ROLES[0],
  status: "draft",
})
